import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { useLogDate } from 'src/features/hooks';

import { getUserId } from 'src/features/profile/store';
import { millisecondsToHours } from 'src/utils/dateUtils';
import { isDesktop } from 'src/utils/devices';
import { getViolationTypes } from 'src/constants/violationConstants';
import { MILLISECONDS_IN_DAY } from 'src/components/LogView/utils';

import { Button } from 'src/components/Button';
import { HosEventListItem } from 'src/components/HosEventListItem/HosEventListItem';
import { PendingEditsHeader, PendingEditsTable } from 'src/features/hosLogs/hosLog/components';

import { ViolationsIcon, ClockIcon } from 'src/assets/hosLogs/hosLog';

import {
  approveRejectPendingEditsAction,
  getExceptions,
  getHosLog,
  getPendingEdits,
  getPendingExceptions,
} from '../store';
import {
  setIsAddingException,
  setInsertingDutyStatus,
  setEditingStatus,
  setActiveViolationId,
} from '../store/hosLogStore';

import { Violation } from '../types';
import { HosGridObject } from '../hosLog/types';

import { ExceptionsList } from '../exceptionsList/ExceptionsList';
import { useGetIsCurrentStatus } from './hooks';

import './HosEventsTab.scss';

interface Props {
  onSaveEdits?: () => void;
  allowOdometerEdits: boolean;
}
export const HosEventsTab: React.FC<Props> = ({ onSaveEdits, allowOdometerEdits }) => {
  const dispatch = useDispatch();
  const [showPendingEdits, setShowPendingEdits] = useState(false);

  const selectedHosLog = useSelector(getHosLog);
  const exceptions = useSelector(getExceptions);
  const pendingExceptions = useSelector(getPendingExceptions);
  const employeeId = useSelector(getUserId);
  const [logDate] = useLogDate();
  const pendingEdits = useSelector(getPendingEdits);

  const validExceptionsLength = useMemo(() => exceptions.length + pendingExceptions.length, [
    exceptions.length,
    pendingExceptions.length,
  ]);
  const hasExceptions = exceptions.length > 0 || pendingExceptions.length > 0;
  const violations: Violation[] = get(selectedHosLog, 'violations', []);
  const hasViolations = violations.length > 0;

  const showTable = selectedHosLog.hosGridObject || hasViolations || hasExceptions;

  const VIOLATION_TYPES = getViolationTypes();
  const exemptStatus = get(selectedHosLog, 'exemptStatus', false);
  const rulesetDescription = get(selectedHosLog, 'rulesetDescription', '');
  const exemptReason = get(selectedHosLog, 'exemptReason', '');
  const ruleSetLabel = useMemo(
    () =>
      exemptStatus
        ? `ELD Exempt, "${exemptReason}".`
        : `Log Ruleset: ${rulesetDescription || 'Unknown'}`,
    [exemptStatus, rulesetDescription, exemptReason],
  );

  useEffect(() => {
    if (pendingEdits.pendingEditsList.length > 0) {
      setShowPendingEdits(true);
    } else {
      setShowPendingEdits(false);
    }
  }, [pendingEdits]);

  const calculateViolationsDuration = useCallback(
    (violations: Violation[]) => {
      const violationDuration = [];

      let cLowerBound = 0;
      let cUpperBound = 0;

      const currentDay = moment.tz(selectedHosLog.logDate, selectedHosLog.timeZone).valueOf();
      const nextDay = currentDay + MILLISECONDS_IN_DAY;

      for (let i = 0; i < violations.length; i++) {
        const lowerBound =
          violations[i].startTime < currentDay ? currentDay : violations[i].startTime;
        let upperBound;
        if (violations[i].endTime) {
          upperBound = violations[i].endTime > nextDay ? nextDay : violations[i].endTime;
        } else {
          upperBound = Date.now() > nextDay ? nextDay : Date.now();
        }

        if (i === 0) {
          cLowerBound = lowerBound;
          cUpperBound = upperBound;

          if (violations.length === 1) {
            violationDuration.push(upperBound - lowerBound);
            break;
          }
        } else {
          if (lowerBound > cUpperBound) {
            violationDuration.push(cUpperBound - cLowerBound);
            cLowerBound = lowerBound;
            cUpperBound = upperBound;
          } else if (upperBound >= cUpperBound && i + 1 < violations.length) {
            cUpperBound = upperBound;
          } else if (i + 1 === violations.length) {
            violationDuration.push(cUpperBound - cLowerBound);
          }
        }
      }
      return violationDuration;
    },
    [selectedHosLog],
  );

  const violationDurationSum = useMemo(() => {
    if (!hasViolations) return;
    const sortedViolations = violations.slice().sort((a, b) => {
      return a.startTime - b.startTime;
    });
    const durationArr = calculateViolationsDuration(sortedViolations);
    const durationSum = durationArr.reduce((sum, item) => sum + item, 0);
    return millisecondsToHours(durationSum, true);
  }, [violations, hasViolations, calculateViolationsDuration]);

  const onViolationHoverHandler = useCallback(
    (violationID: string, hover: boolean) => (): void => {
      dispatch(setActiveViolationId(hover ? violationID : ''));
    },
    [dispatch],
  );

  const onOpenAddExceptions = useCallback(() => {
    dispatch(setIsAddingException(true));
  }, [dispatch]);

  const onOpenInsertDutyStatus = useCallback(() => {
    dispatch(setInsertingDutyStatus(true));
  }, [dispatch]);

  const handleEditHosGridObject = useCallback(
    (item: HosGridObject) => {
      dispatch(setEditingStatus(item));
    },
    [dispatch],
  );

  const approveRejectPendingEditsHandler = useCallback(
    (status, reason) => {
      const editId = pendingEdits.pendingEditsList[0].id;
      const checksum = pendingEdits.pendingEditsList[0].checksum;
      dispatch(
        approveRejectPendingEditsAction({
          logId: `${employeeId}:${logDate}`,
          approveRejectData: { editId, status, reason, checksum },
        }),
      );
    },
    [pendingEdits, employeeId, logDate, dispatch],
  );

  const getIsCurrentStatus = useGetIsCurrentStatus(moment(), selectedHosLog);

  const { certifiedStatus = 0 } = selectedHosLog;

  return (
    <div className="log-info">
      <div className="edit-individual-log">
        <div className="edit-individual-log__event-log-button">
          Exceptions
          {!exemptStatus && !certifiedStatus && (
            <Button
              title="Add Exceptions"
              className="log-button"
              onClick={onOpenAddExceptions}
              disabled={validExceptionsLength >= 2}
            />
          )}
        </div>
        <div className="edit-individual-log__exceptions-content">
          {hasExceptions ? (
            <table>
              <ExceptionsList
                exceptions={exceptions}
                pendingExceptions={pendingExceptions}
                employeeId={employeeId}
                logDate={logDate}
                onSaveEdits={onSaveEdits}
              />
            </table>
          ) : (
            <div className="edit-individual-log__exceptions-description">
              No exceptions invoked today. Click to add an exception. Ensure at least one exception
              is configured here
            </div>
          )}
        </div>
        <div className="edit-individual-log__event-log-button">
          Duty Status
          {/* Temporarily disabled */ false && !exemptStatus && !certifiedStatus && (
            <Button
              title="Insert Duty Status"
              className="log-button"
              onClick={onOpenInsertDutyStatus}
              disabled={true}
            />
          )}
        </div>
        <div className="edit-log">
          <span className="log-ruleset">{ruleSetLabel}</span>
        </div>
        {showPendingEdits && (
          <PendingEditsHeader approveRejectHandler={approveRejectPendingEditsHandler} />
        )}
      </div>

      {showTable && (
        <table>
          {hasViolations && (
            <>
              <tbody className="log-info-tbody-title">
                <tr>
                  <td colSpan={7}>
                    <div className="log-info-table-title">
                      Violations
                      <div className="log-info-violation-info">
                        <img src={ViolationsIcon} alt="ViolationsIcon" />
                        {violations.length}
                        <img src={ClockIcon} alt="ClockIcon" />
                        {violationDurationSum}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody className="log-info-table-thead">
                <tr>
                  <th>Status</th>
                  <th>Start Time</th>
                  <th>Duration</th>
                  <th>Location</th>
                  <th colSpan={2}>Violation</th>
                </tr>
              </tbody>
              <tbody className="log-info-tbody-content">
                {violations.map(item => {
                  const itemTimeZone = selectedHosLog.timeZone;
                  const timeWithTimeZone = moment.tz(item.startTime, itemTimeZone);
                  const startTimeText = `${timeWithTimeZone.format('MMM D, HH:mm')} ${moment()
                    .tz(itemTimeZone)
                    .format('z')}`;
                  const endTime = item.endTime ? item.endTime : Date.now();
                  const duration = `${millisecondsToHours(endTime - item.startTime)} HRS`;
                  return (
                    <tr
                      key={item.violationId}
                      className={classNames('log-info-box', 'log-info-box__violation', {
                        'is-desktop': isDesktop,
                      })}
                      onMouseEnter={onViolationHoverHandler(item.violationId, true)}
                      onMouseLeave={onViolationHoverHandler(item.violationId, false)}
                    >
                      <td className={classNames('log-info-header', 'log-info-header__v')}>V</td>
                      <td data-title="Start Time">{startTimeText}</td>
                      <td data-title="Duration">{duration}</td>
                      <td data-title="Location">
                        {item.driverLocation ? item.driverLocation : '--'}
                      </td>
                      <td colSpan={3} data-title="Violation">
                        {VIOLATION_TYPES[item.violationType]}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </>
          )}

          {showPendingEdits && pendingEdits.pendingEditsList[0] ? (
            <PendingEditsTable
              pendingEdits={pendingEdits}
              allowOdometerEdits={allowOdometerEdits}
            />
          ) : (
            <>
              {selectedHosLog.hosGridObject && (
                <>
                  <tbody className="log-info-tbody-title">
                    <tr>
                      <td colSpan={8}>
                        <div className="log-info-table-title">Duty Status</div>
                      </td>
                    </tr>
                  </tbody>
                  {isDesktop && (
                    <>
                      <tbody className="log-info-table-thead">
                        <tr>
                          <th>Status</th>
                          <th>Time</th>
                          <th>Duration</th>
                          <th>Location</th>
                          <th>PC/YM</th>
                          <th>Note</th>
                          {allowOdometerEdits && <th>Odometer</th>}
                          <th />
                          <th />
                        </tr>
                      </tbody>
                    </>
                  )}
                  <tbody className="log-info-tbody-content">
                    {selectedHosLog.hosGridObject.map((hosGridObject, index, arr) => {
                      return (
                        <HosEventListItem
                          logData={selectedHosLog}
                          hosGridObject={hosGridObject}
                          index={index}
                          nextHosGridObject={arr[index + 1]}
                          key={hosGridObject.time + index}
                          onEdit={handleEditHosGridObject}
                          isCurrentStatus={getIsCurrentStatus(index)}
                          logDate={logDate}
                          timeZone={selectedHosLog.timeZone}
                          allowOdometerEdits={allowOdometerEdits}
                        />
                      );
                    })}
                  </tbody>
                </>
              )}
            </>
          )}
        </table>
      )}
    </div>
  );
};
